<template>
  <div class="custom-menu-check">
    <div class="custom-menu-container">
      <case-logic-label>Custom menu</case-logic-label>
      <md-field>
        <md-select
          v-model="state.id"
          v-close-on-zoom
          class="grandparent-select"
          :disabled="readonly"
          @md-selected="selectGrandparent"
        >
          <md-option
            v-for="button in grandParentButtons"
            :key="button.value"
            :value="button.value"
          >
            {{ button.label }}
          </md-option>
        </md-select>
      </md-field>
      <md-field>
        <md-select
          v-model="state.parentId"
          v-close-on-zoom
          class="parent-select"
          placeholder="select parent button"
          :disabled="readonly || !parentButtons.length"
          @md-selected="selectParent"
        >
          <md-option
            v-for="button in parentButtons"
            :key="button.value"
            :value="button.value"
          >
            {{ button.label }}
          </md-option>
        </md-select>
      </md-field>
      <help-tooltip v-if="parentButtonError" :show-space="false" type="error">
        <span>{{ parentButtonError.message }}</span>
      </help-tooltip>
      <md-field>
        <md-select
          v-model="state.childId"
          v-close-on-zoom
          class="child-select"
          :disabled="readonly || !childButtons.length"
          @md-selected="selectChild"
        >
          <md-option
            v-for="button in childButtons"
            :key="button.value"
            :value="button.value"
          >
            {{ button.label }}
          </md-option>
        </md-select>
      </md-field>
      <help-tooltip v-if="childButtonError" :show-space="false" type="error">
        <span>{{ childButtonError.message }}</span>
      </help-tooltip>
    </div>
    <div class="count-container">
      <case-logic-label>Was selected</case-logic-label>
      <md-field>
        <md-select
          id="operator-select"
          v-model="state.operator"
          v-close-on-zoom
          class="operator-select"
          placeholder="exactly"
          :disabled="readonly"
          @md-selected="selectOperator"
        >
          <md-option
            v-for="op in operators"
            :key="op.value"
            :value="op.value"
          >
            {{ op.label }}
          </md-option>
        </md-select>
      </md-field>
      <md-field>
        <md-input
          v-model="state.count"
          type="number"
          min="0"
          class="count-input"
          placeholder="any amount of"
          :disabled="readonly"
          @input="value => inputCount(parseInt(value))"
        />
      </md-field>
      <case-logic-label>times</case-logic-label>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';

import HelpTooltip from '~lib/common/components/HelpTooltip';

import CaseLogicLabel from './common/CaseLogicLabel.vue';
import checkMixins from './mixins/CheckMixins';

export default {
  name: 'custom-menu-check',
  components: {
    HelpTooltip,
    CaseLogicLabel
  },
  mixins: [ checkMixins ],
  props: [
    'readonly',
    'emitter',
    'ikey',
    'getData',
    'putData',
    'control',
  ],
  data() {
    return {
      grandParentButtons: [
        { value: 'ASSESS', label: 'Assess' },
        { value: 'MONITOR', label: 'Monitor' },
        { value: 'TREAT', label: 'Treat' },
      ],
      operators: [
        { value: 'LESS_THAN', label: 'less than' },
        { value: 'EQUAL', label: 'exactly' },
        { value: 'GREATER_THAN', label: 'more than' },
      ],
      state: {
        id: null,
        parentId: null,
        childId: null,
        operator: null,
      },
    };
  },
  computed: {
    customMenus() {
      return this.emitter.caseConf.customMenus || [];
    },
    parentButtons() {
      const grandParentMenu = this.customMenus.find(menu => menu.category === this.state.id);
      if (!grandParentMenu) {
        return [];
      }
      const buttons = grandParentMenu.items.map(item => ({
        value: item.id,
        label: item.content.en
      }));
      if (this.state.parentId && !grandParentMenu.items.find(item => item.id === this.state.parentId)) {
        buttons.push({ value: this.state.parentId, label: '-- not found --' });
      }
      return buttons;
    },
    childItems() {
      const grandParentMenu = this.customMenus.find(menu => menu.category === this.state.id);
      if (!grandParentMenu) {
        return [];
      }
      const parentMenu = grandParentMenu.items.find(item => item.id === this.state.parentId);
      if (!parentMenu) {
        return [];
      }
      return parentMenu.children;
    },
    childButtons() {
      const buttons = this.childItems.map(item => ({
        value: item.id,
        label: item.content.en
      }));
      if (!buttons.length) {
        return [];
      }
      if (this.state.childId && !buttons.find(item => item.value === this.state.childId)) {
        buttons.push({ value: this.state.childId, label: '-- not found --'});
      }
      buttons.push({ value: undefined, label: '-- clear --' });
      return buttons;
    },
    parentButtonError() {
      return this.errors.find(error => error.metadata.errorId === 'customMenuParent');
    },
    childButtonError() {
      return this.errors.find(error => error.metadata.errorId === 'customMenuChild');
    },
  },
  mounted() {
    const data = this.getData(this.ikey);
    this.state.id = get(data, 'id') || 'ASSESS';
    this.state.parentId = get(data, 'parentId');
    this.state.childId = get(data, 'childId');
    this.state.operator = get(data, 'operator') || 'EQUAL';
    this.state.count = get(data, 'count');
    this.update();
  },
  methods: {
    selectGrandparent(value) {
      this.state.id = value;
      this.state.parentId = null;
      this.state.childId = null;
      this.update();
    },
    selectParent(value) {
      this.state.parentId = value;
      this.state.childId = null;
      this.update();
    },
    selectChild(value) {
      this.state.childId = value;
      this.update();
    },
    selectOperator(value) {
      this.state.operator = value;
      this.update();
    },
    inputCount(value) {
      this.state.count = value;
      this.update();
    },
    update() {
      this.putData(this.ikey, {
        type: 'CUSTOM_MENU',
        id: this.state.id, // grandparent
        parentId: this.state.parentId,
        childId: this.state.childId,
        operator: this.state.operator,
        count: this.state.count
      });
      this.emitter.trigger('process', this.control);
    }
  }
}
</script>

<style scoped>
.custom-menu-check {
  display: flex;
  flex-direction: column;
}

.custom-menu-container,
.count-container {
  display: flex;
  align-items: flex-start;
}

.custom-menu-container .md-field {
  margin: 0;
}
.count-container .md-field {
  margin: 0;
}

.md-select >>> .md-input {
  height: 36px !important;
}
.md-input {
  height: 36px !important;
}
.parent-select,
.child-select {
  min-width: 220px;
}
</style>